import { z } from 'zod';
import IRoundConfiguration from '../../../interfaces/round-configuration/IRoundConfiguration';
import IPoolConfiguration from '../../../interfaces/pool-configuration/IPoolConfiguration';
import { getRoundManagerApiAxiosInstance } from '../../axios-config';
import IApiResponse from '../../../interfaces/IApiResponse';
import i18n from '../../../../lib/i18n';

const requiredFieldMessage = 'RequiredFieldMessage';
const digitsOnlyFieldMessage = 'DigitsOnlyFieldMessage';
const timeFieldMesage = 'TimeFieldMessage';
const timeFormatFieldMessage = 'TimeFormatFieldMessage';
const confirmationDuration2ndMessage = 'ConfirmationDuration2ndMessage';
const confirmationDuration3rdMessage = 'ConfirmationDuration3rdMessage';

export const getAllRoundConfigurations = async (): Promise<
    IApiResponse<IRoundConfiguration[]>
> => {
    try {
        const Api = await getRoundManagerApiAxiosInstance();

        const response = await Api.get(`v1/rounds/configurations`);

        if (response.data) {
            return { response: response.data };
        }

        return { error: new Error(i18n.t('ErrorGetingRoundsConfigurations')) };
    } catch (error) {
        return {
            error: new Error((error as { message: string }).message),
        };
    }
};

export const updateRoundConfiguration = async (
    roundConfiguration: IRoundConfiguration,
): Promise<IApiResponse<boolean>> => {
    try {
        const Api = await getRoundManagerApiAxiosInstance();

        await Api.put(
            `v1/rounds/configurations/${roundConfiguration.id}`,
            roundConfiguration,
        );

        return { response: true };
    } catch (e: any) {
        const validationErrors = e?.response?.data;

        if (Array.isArray(validationErrors) && validationErrors.length > 0) {
            const error = validationErrors[validationErrors.length - 1];
            if (error?.Type === 'Validation' && error?.Error) {
                return {
                    error: new Error(i18n.t(error.Error)),
                };
            }
        }
        return {
            error: new Error(i18n.t('ErrorUpdatingRoundConfiguration')),
        };
    }
};

export const timespanToSeconds = (timespan: string): number => {
    const [hours, minutes, seconds] = timespan.split(':').map(Number);
    return hours * 3600 + minutes * 60 + seconds;
};

export const roundConfigurationSchema = z
    .object({
        penaltyRoundsQuantity: z
            .string()
            .min(1, requiredFieldMessage)
            .regex(/^\d+$/, digitsOnlyFieldMessage)
            .or(z.number()),
        beginningTime: z
            .string()
            .length(8, timeFieldMesage)
            .regex(/^\d{2}:\d{2}:\d{2}$/, timeFormatFieldMessage),
        acceptanceDuration: z
            .string()
            .length(8, timeFieldMesage)
            .regex(/^\d{2}:\d{2}:\d{2}$/, timeFormatFieldMessage),
        defaultAcceptanceDuration: z
            .string()
            .length(8, timeFieldMesage)
            .regex(/^\d{2}:\d{2}:\d{2}$/, timeFormatFieldMessage),
        defaultConfirmationDuration: z
            .string()
            .length(8, timeFieldMesage)
            .regex(/^\d{2}:\d{2}:\d{2}$/, timeFormatFieldMessage),
        confirmationDuration2nd: z
            .string()
            .length(8, timeFieldMesage)
            .regex(/^\d{2}:\d{2}:\d{2}$/, timeFormatFieldMessage),
        confirmationDuration3rd: z
            .string()
            .length(8, timeFieldMesage)
            .regex(/^\d{2}:\d{2}:\d{2}$/, timeFormatFieldMessage),
    })
    .superRefine((data, ctx) => {
        const defaultConfirmationInSeconds = timespanToSeconds(
            data.defaultConfirmationDuration,
        );
        const confirmationDuration2ndInSeconds = timespanToSeconds(
            data.confirmationDuration2nd,
        );
        const confirmationDuration3rdInSeconds = timespanToSeconds(
            data.confirmationDuration3rd,
        );

        if (defaultConfirmationInSeconds < confirmationDuration2ndInSeconds) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: confirmationDuration2ndMessage,
                path: ['confirmationDuration2nd'],
            });
        }

        if (defaultConfirmationInSeconds < confirmationDuration3rdInSeconds) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: confirmationDuration3rdMessage,
                path: ['confirmationDuration3rd'],
            });
        }

        if (
            confirmationDuration2ndInSeconds < confirmationDuration3rdInSeconds
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: confirmationDuration3rdMessage,
                path: ['confirmationDuration3rd'],
            });
        }
    });

const maxPoolTimeLimit = '22:30:00';
const maxPoolTimeLimitMessage = 'MaxPoolTimeLimitMessage';

export const getAllPoolConfigurations = async (): Promise<
    IApiResponse<IPoolConfiguration[]>
> => {
    try {
        const Api = await getRoundManagerApiAxiosInstance();

        const response = await Api.get(`v1/pool/configurations`);

        if (response.data) {
            return { response: response.data };
        }

        return { error: new Error(i18n.t('ErrorGetingPoolConfigurations')) };
    } catch (error) {
        return {
            error: new Error((error as { message: string }).message),
        };
    }
};

export const updatePoolConfiguration = async (
    poolConfiguration: IPoolConfiguration,
): Promise<IApiResponse<boolean>> => {
    try {
        const Api = await getRoundManagerApiAxiosInstance();

        await Api.put(
            `v1/pool/configurations/${poolConfiguration.id}`,
            poolConfiguration,
        );

        return { response: true };
    } catch (e: any) {
        const businessExceptionMessage =
            e?.response?.data[0]?.Type === 'BusinessException'
                ? e?.response?.data[0]?.Error
                : '';

        return {
            error: new Error(
                `${i18n.t(
                    'ErrorUpdatingPoolConfiguration',
                )} ${businessExceptionMessage}`,
            ),
        };
    }
};

export const poolConfigurationSchema = z.object({
    finishedProductRoundsQuantityLimit: z
        .string()
        .regex(/^\d+$/, digitsOnlyFieldMessage)
        .or(z.number()),
    directSellingRoundsQuantityLimit: z
        .string()
        .regex(/^\d+$/, digitsOnlyFieldMessage)
        .or(z.number()),
    inputsRoundsQuantityLimit: z
        .string()
        .regex(/^\d+$/, digitsOnlyFieldMessage)
        .or(z.number()),
    finishedProductTimeLimit: z
        .string()
        .length(8, timeFieldMesage)
        .regex(/^\d{2}:\d{2}:\d{2}$/, timeFormatFieldMessage)
        .refine(time => time <= maxPoolTimeLimit, maxPoolTimeLimitMessage),
    directSellingTimeLimit: z
        .string()
        .length(8, timeFieldMesage)
        .regex(/^\d{2}:\d{2}:\d{2}$/, timeFormatFieldMessage)
        .refine(time => time <= maxPoolTimeLimit, maxPoolTimeLimitMessage),
    inputsTimeLimit: z
        .string()
        .length(8, timeFieldMesage)
        .regex(/^\d{2}:\d{2}:\d{2}$/, timeFormatFieldMessage)
        .refine(time => time <= maxPoolTimeLimit, maxPoolTimeLimitMessage),
});
